import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  TextField,
  Paper,
  Typography,
  Alert,
  Snackbar,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  getOrder,
  createOrder,
  updateOrder,
  addOrderLine,
  addOrderLineItem,
  updateOrderLine,
  deleteOrderLine,
  updateOrderLineItem,
  deleteOrderLineItem,
  upsertOrderLineItemCustomizationFields,
  deleteOrderLineItemCustomizationField,
  getCustomers,
  getProducts,
  getSKUs,
  createCustomer,
  getSettings,
  uploadMedia,
} from '../app/api';
import ConfirmDialog from '../components/ConfirmDialog';
import MediaGallery from '../components/MediaGallery';
import { useTranslation } from '../app/LanguageContext';
import { useWorkspace } from '../app/WorkspaceContext';
import { formatCurrency, handleCurrencyInputChange, parseCurrency, handlePercentInputChange, parsePercent, formatPercent } from '../app/currency';
import { addBusinessDays, formatDateISO } from '../app/businessDays';
import { isoToDisplayBR, displayBRToISO, handleDateInputChange, apiDateTimeToDisplay, displayDateTimeToApi, handleDateTimeInputChange, getDateTimeInputPlaceholder } from '../app/date';

const OrderFormPage = () => {
  const { translate } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const autoSaveTimeoutRef = useRef(null);
  const pasteHandlerRef = useRef(null);
  const creatingRef = useRef(false);
  const formItemSaveTimeoutsRef = useRef({});

  const [order, setOrder] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [skusByProduct, setSkusByProduct] = useState({});
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    valid_until: '',
    status: 'DRAFT',
    observations: '',
    discount_value: null,
    created_at: '',
  });
  const [formItems, setFormItems] = useState([{ id: 1, sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '' }]);
  const formItemsRef = useRef([{ id: 1, sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '' }]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isNewOrder, setIsNewOrder] = useState(false);
  const [customerInputText, setCustomerInputText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, type: null, lineId: null, itemId: null });
  const [discountPercentInput, setDiscountPercentInput] = useState('');
  const [discountValueInput, setDiscountValueInput] = useState('');
  const [finalValueInput, setFinalValueInput] = useState('');
  const [discountFieldFocused, setDiscountFieldFocused] = useState(null);
  const [mediaRefreshKey, setMediaRefreshKey] = useState(0);
  const [itemCustomizationExpanded, setItemCustomizationExpanded] = useState({});
  const [newItemCustomFieldModal, setNewItemCustomFieldModal] = useState({ open: false, formItemId: null });
  const [newItemCustomField, setNewItemCustomField] = useState({ name: '', key: '', value_type: 'text' });
  const itemCustomizationSaveTimeoutsRef = useRef({});

  useEffect(() => {
    loadCustomers();
    loadProducts();
    if (!id && !creatingRef.current && !order) {
      setIsNewOrder(true);
      loadDefaultValidUntil().then((validUntil) => {
        if (validUntil) {
          setFormData((f) => ({ ...f, valid_until: validUntil }));
          createOrderAutomatically(validUntil);
        } else {
          createOrderAutomatically(null);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      const isNewlyCreated = sessionStorage.getItem(`newOrder_${id}`);
      if (isNewlyCreated === 'true') {
        setIsNewOrder(true);
        sessionStorage.removeItem(`newOrder_${id}`);
      } else {
        setIsNewOrder(false);
      }
      loadOrder();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFileUpload = useCallback(async (files) => {
    if (!order?.id) return;
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('metadata_key', 'order_attachment');
        formData.append('metadata_value', order.id);
        
        await uploadMedia(formData);
      }

      setMediaRefreshKey((k) => k + 1);
      setSnackbar({ open: true, message: translate('media.uploaded'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  }, [order?.id, translate]);

  useEffect(() => {
    const handlePaste = (e) => {
      if (!order?.id) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            handleFileUpload([file]);
          }
        }
      }
    };

    if (order?.id) {
      document.addEventListener('paste', handlePaste);
      pasteHandlerRef.current = handlePaste;
    }

    return () => {
      if (pasteHandlerRef.current) {
        document.removeEventListener('paste', pasteHandlerRef.current);
      }
    };
  }, [order?.id, handleFileUpload]);

  const createOrderAutomatically = async (validUntil = null) => {
    if (creatingRef.current || order) return;
    creatingRef.current = true;
    setCreating(true);
    try {
      let currentValidUntil = validUntil || (formData.valid_until ? displayBRToISO(formData.valid_until) : null) || null;
      if (currentValidUntil === '') {
        currentValidUntil = null;
      }
      const res = await createOrder({
        customer_id: '',
        title: '',
        valid_until: currentValidUntil,
        created_by: '',
        lines: [],
      });
      setOrder(res.data);
      if (res.data.valid_until) {
        const validUntilDisplay = isoToDisplayBR(res.data.valid_until);
        setFormData((f) => ({ ...f, valid_until: validUntilDisplay }));
      } else if (validUntil) {
        setFormData((f) => ({ ...f, valid_until: isoToDisplayBR(validUntil) }));
      }
      
      if (res.data && (!res.data.lines || res.data.lines.length === 0)) {
        await addOrderLine(res.data.id, { sequence: 1, description: '' });
        const updatedRes = await getOrder(res.data.id);
        setOrder(updatedRes.data);
        
        const defaultItem = [{ id: 1, itemId: null, lineId: null, sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '', customization_fields: [], customizationValues: {}, selectOtherActive: {} }];
        setFormItems(defaultItem);
        formItemsRef.current = defaultItem;
      } else {
        const savedItems = (res.data?.lines || []).flatMap((line) =>
          (line.items || []).map((item) => {
            const displayPrice = item.unit_price > 0 ? formatCurrency(item.unit_price) : '';
            return {
              id: `saved_${item.id}`,
              itemId: item.id,
              lineId: line.id,
              sku_id: item.sku_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
              unit_priceDisplay: displayPrice,
              description: item.description || '',
            };
          })
        );
        
        if (savedItems.length > 0) {
          setFormItems(savedItems);
          formItemsRef.current = savedItems;
        } else {
          const defaultItem = [{ id: 1, itemId: null, lineId: null, sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '', customization_fields: [], customizationValues: {}, selectOtherActive: {} }];
          setFormItems(defaultItem);
          formItemsRef.current = defaultItem;
        }
      }
      
      sessionStorage.setItem(`newOrder_${res.data.id}`, 'true');
      navigate(`/orders/${res.data.id}/edit`, { replace: true });
    } catch (err) {
      if (err.message && !err.message.includes('already exists')) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      }
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await getCustomers();
      setCustomers(res.data?.customers || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadDefaultValidUntil = async () => {
    try {
      const res = await getSettings();
      const days = res.data?.order_default_validity_days ?? 7;
      if (days > 0) {
        const validDate = addBusinessDays(new Date(), days);
        const validUntilISO = formatDateISO(validDate);
        const validUntilDisplay = isoToDisplayBR(validUntilISO);
        setFormData((f) => ({ ...f, valid_until: validUntilDisplay }));
        return validUntilISO;
      }
      return '';
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  const loadProducts = async () => {
    try {
      const res = await getProducts();
      setProducts(res.data?.products || []);
      for (const p of res.data?.products || []) {
        try {
          const skuRes = await getSKUs(p.id);
          setSkusByProduct((prev) => ({ ...prev, [p.id]: skuRes.data ?? [] }));
        } catch (_) {}
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadOrder = async () => {
    try {
      const res = await getOrder(id);
      if (!res.data) {
        setSnackbar({ open: true, message: 'Pedido não encontrado', severity: 'error' });
        return;
      }
      
      setOrder(res.data);
      const customerId = res.data?.customer_id || '';
      const customerName = res.data?.customer_name || '';
      setFormData({
        customer_id: customerId,
        title: res.data?.title || '',
        valid_until: res.data?.valid_until ? isoToDisplayBR(res.data.valid_until) : '',
        status: res.data?.status || 'DRAFT',
        observations: res.data?.observations || '',
        discount_value: res.data?.discount_value ?? null,
        created_at: res.data?.created_at ? apiDateTimeToDisplay(res.data.created_at) : '',
      });
      if (customerId && customerName) {
        setCustomerInputText(customerName);
      } else {
        setCustomerInputText('');
      }
      
      let orderData = res.data;
      if (orderData && (!orderData.lines || orderData.lines.length === 0)) {
        await addOrderLine(orderData.id, { sequence: 1, description: '' });
        const updatedRes = await getOrder(id);
        orderData = updatedRes.data;
        setOrder(orderData);
      }
      
      const savedItems = (orderData?.lines || []).flatMap((line) =>
        (line.items || []).map((item) => {
          const displayPrice = item.unit_price > 0 ? formatCurrency(item.unit_price) : '';
          const itemCustomizationFields = item.customization_fields || [];
          const itemCustomizationValues = Object.fromEntries(
            itemCustomizationFields.map((f) => [f.key, f.value || ''])
          );
          const itemSelectOtherActive = {};
          itemCustomizationFields.forEach((f) => {
            if (f.value_type === 'select' && f.allow_other && f.value && f.options && !f.options.includes(f.value)) {
              itemSelectOtherActive[f.key] = true;
            }
          });
          return {
            id: `saved_${item.id}`,
            itemId: item.id,
            lineId: line.id,
            sku_id: item.sku_id,
            original_sku_id: item.sku_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            unit_priceDisplay: displayPrice,
            description: item.description || '',
            customization_fields: itemCustomizationFields,
            customizationValues: itemCustomizationValues,
            selectOtherActive: itemSelectOtherActive,
          };
        })
      );
      
      if (savedItems.length > 0) {
        setFormItems(savedItems);
        formItemsRef.current = savedItems;
      } else {
        const defaultItem = [{ id: 1, itemId: null, lineId: null, sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '', customization_fields: [], customizationValues: {}, selectOtherActive: {} }];
        setFormItems(defaultItem);
        formItemsRef.current = defaultItem;
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const autoSave = useCallback(() => {
    if (!order?.id) return;
    
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        const validUntilISO = formData.valid_until ? displayBRToISO(formData.valid_until) : null;
        const payload = {
          status: formData.status,
          title: formData.title || null,
          valid_until: validUntilISO,
          observations: formData.observations || null,
          customer_id: formData.customer_id || null,
          discount_value: formData.discount_value,
        };
        if (['DRAFT', 'SENT'].includes(formData.status)) {
          payload.created_at = formData.created_at ? displayDateTimeToApi(formData.created_at) : null;
        }
        await updateOrder(order.id, payload);
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    }, 500);
  }, [order?.id, formData.status, formData.title, formData.valid_until, formData.observations, formData.customer_id, formData.discount_value, formData.created_at]);

  useEffect(() => {
    if (order?.id) {
      autoSave();
    }
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [autoSave, order?.id]);

  const handleUpdateStatus = async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const validUntilISO = formData.valid_until ? displayBRToISO(formData.valid_until) : null;
      const payload = {
        status: formData.status,
        title: formData.title || null,
        valid_until: validUntilISO,
        observations: formData.observations || null,
        customer_id: formData.customer_id || null,
        discount_value: formData.discount_value,
      };
      if (['DRAFT', 'SENT'].includes(formData.status)) {
        payload.created_at = formData.created_at ? displayDateTimeToApi(formData.created_at) : null;
      }
      await updateOrder(order.id, payload);
      setSnackbar({ open: true, message: translate('orders.updated'), severity: 'success' });
      loadOrder();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = async (_, option) => {
    let customerId = '';
    if (option && typeof option === 'object') {
      customerId = option.id || '';
      setCustomerInputText(option.name || '');
    } else if (typeof option === 'string' && option.trim()) {
      try {
        const customerRes = await createCustomer({
          name: option.trim(),
          email: '',
          phone: '',
          document: '',
          address: '',
        });
        customerId = customerRes.data?.id || '';
        await loadCustomers();
        const newCustomer = customers.find(c => c.id === customerId) || { id: customerId, name: option.trim() };
        setCustomerInputText(newCustomer.name);
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
        return;
      }
    } else {
      setCustomerInputText('');
    }
    
    setFormData((f) => ({ ...f, customer_id: customerId }));
  };

  const handleAddNewFormLine = () => {
    setFormItems(prevItems => {
      const maxId = Math.max(...prevItems.map(item => {
        const numId = typeof item.id === 'string' && item.id.startsWith('saved_') ? 0 : (typeof item.id === 'number' ? item.id : 0);
        return numId;
      }), 0);
      const newId = maxId + 1;
      const newItems = [...prevItems, { id: newId, itemId: null, lineId: null, sku_id: '', quantity: 1, unit_price: 0, unit_priceDisplay: '', description: '', customization_fields: [], customizationValues: {}, selectOtherActive: {} }];
      formItemsRef.current = newItems;
      return newItems;
    });
  };

  const handleSaveFormItem = async (formItemId) => {
    if (!order?.id) return;
    
    if (formItemSaveTimeoutsRef.current[formItemId]) {
      clearTimeout(formItemSaveTimeoutsRef.current[formItemId]);
      delete formItemSaveTimeoutsRef.current[formItemId];
    }
    
    const formItem = formItemsRef.current.find(item => item.id === formItemId);
    const hasProduct = formItem?.sku_id;
    const hasFreeItemDescription = formItem?.description?.trim();
    const canSave = formItem && formItem.quantity >= 1 && formItem.unit_price >= 0 && (hasProduct || hasFreeItemDescription);
    if (!canSave) return;
    
    if (formItem.itemId && formItem.lineId) {
      const skuChanged = formItem.original_sku_id !== undefined && formItem.sku_id !== formItem.original_sku_id;
      if (skuChanged) {
        setLoading(true);
        try {
          await deleteOrderLineItem(order.id, formItem.lineId, formItem.itemId);
          const newItemRes = await addOrderLineItem(order.id, formItem.lineId, {
            sku_id: formItem.sku_id || '',
            quantity: formItem.quantity,
            unit_price: formItem.unit_price || 0,
            description: formItem.description || '',
          });
          const updatedOrder = await getOrder(order.id);
          setOrder(updatedOrder.data);
          const newItemFromOrder = updatedOrder.data?.lines?.flatMap((l) => l.items || []).find((i) => i.id === newItemRes.data?.id);
          const itemCustomizationFields = newItemFromOrder?.customization_fields || [];
          const itemCustomizationValues = Object.fromEntries(itemCustomizationFields.map((f) => [f.key, f.value || '']));
          const itemSelectOtherActive = {};
          itemCustomizationFields.forEach((f) => {
            if (f.value_type === 'select' && f.allow_other && f.value && f.options && !f.options.includes(f.value)) {
              itemSelectOtherActive[f.key] = true;
            }
          });
          setFormItems(prevItems => {
            const currentItem = prevItems.find(item => item.id === formItemId);
            const updated = prevItems.map(item => 
              item.id === formItemId 
                ? { 
                    ...item, 
                    id: `saved_${newItemRes.data?.id || formItemId}`,
                    itemId: newItemRes.data?.id || null,
                    original_sku_id: formItem.sku_id,
                    unit_priceDisplay: currentItem?.unit_priceDisplay || (formItem.unit_price > 0 ? formatCurrency(formItem.unit_price) : ''),
                    customization_fields: itemCustomizationFields,
                    customizationValues: itemCustomizationValues,
                    selectOtherActive: itemSelectOtherActive,
                  }
                : item
            );
            formItemsRef.current = updated;
            return updated;
          });
        } catch (err) {
          setSnackbar({ open: true, message: err.message, severity: 'error' });
        } finally {
          setLoading(false);
        }
        return;
      }
      const unitPrice = formItem.unit_price || 0;
      try {
        await updateOrderLineItem(order.id, formItem.lineId, formItem.itemId, {
          quantity: formItem.quantity,
          unit_price: unitPrice,
          description: formItem.description || '',
        });
        
        setFormItems(prevItems => {
          const currentItem = prevItems.find(item => item.id === formItemId);
          const preserveDisplay = currentItem?.unit_priceDisplay && currentItem.unit_priceDisplay.trim() !== '' && currentItem.unit_price > 0;
          const updated = prevItems.map(item => 
            item.id === formItemId 
              ? { 
                  ...item, 
                  unit_price: unitPrice,
                  unit_priceDisplay: preserveDisplay ? currentItem.unit_priceDisplay : (unitPrice > 0 ? formatCurrency(unitPrice) : '')
                }
              : item
          );
          formItemsRef.current = updated;
          return updated;
        });
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      }
      return;
    }
    
    setLoading(true);
    try {
      const unitPrice = formItem.unit_price || 0;

      let targetLineId = null;
      if (!order?.lines || order.lines.length === 0) {
        await addOrderLine(order.id, { sequence: 1, description: '' });
        await loadOrder();
        const updatedOrder = await getOrder(order.id);
        targetLineId = updatedOrder.data?.lines?.[0]?.id;
      } else {
        targetLineId = order.lines[0].id;
      }

      if (targetLineId) {
        const newItemRes = await addOrderLineItem(order.id, targetLineId, {
          sku_id: formItem.sku_id || '',
          quantity: formItem.quantity,
          unit_price: unitPrice,
          description: formItem.description || '',
        });
        
        const updatedOrder = await getOrder(order.id);
        setOrder(updatedOrder.data);
        const newItemFromOrder = updatedOrder.data?.lines?.flatMap((l) => l.items || []).find((i) => i.id === newItemRes.data?.id);
        const itemCustomizationFields = newItemFromOrder?.customization_fields || [];
        const itemCustomizationValues = Object.fromEntries(itemCustomizationFields.map((f) => [f.key, f.value || '']));
        const itemSelectOtherActive = {};
        itemCustomizationFields.forEach((f) => {
          if (f.value_type === 'select' && f.allow_other && f.value && f.options && !f.options.includes(f.value)) {
            itemSelectOtherActive[f.key] = true;
          }
        });

        setFormItems(prevItems => {
          const currentItem = prevItems.find(item => item.id === formItemId);
          const finalPrice = currentItem?.unit_price || unitPrice;
          const finalDisplay = currentItem?.unit_priceDisplay || (finalPrice > 0 ? formatCurrency(finalPrice) : '');
          
          const updated = prevItems.map(item => 
            item.id === formItemId 
              ? { 
                  ...item, 
                  id: `saved_${newItemRes.data?.id || formItemId}`,
                  itemId: newItemRes.data?.id || null,
                  lineId: targetLineId,
                  original_sku_id: formItem.sku_id,
                  unit_price: finalPrice,
                  unit_priceDisplay: finalDisplay,
                  customization_fields: itemCustomizationFields,
                  customizationValues: itemCustomizationValues,
                  selectOtherActive: itemSelectOtherActive,
                }
              : item
          );
          formItemsRef.current = updated;
          return updated;
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFormItem = async (formItemId) => {
    const formItem = formItems.find(item => item.id === formItemId);
    
    if (formItem?.itemId && formItem?.lineId && order?.id) {
      try {
        await deleteOrderLineItem(order.id, formItem.lineId, formItem.itemId);
        await loadOrder();
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      }
      return;
    }
    
    if (formItemsRef.current.length > 1) {
      if (formItemSaveTimeoutsRef.current[formItemId]) {
        clearTimeout(formItemSaveTimeoutsRef.current[formItemId]);
        delete formItemSaveTimeoutsRef.current[formItemId];
      }
      setFormItems(prevItems => {
        const filtered = prevItems.filter(item => item.id !== formItemId);
        formItemsRef.current = filtered;
        return filtered;
      });
    }
  };

  const handleUpdateFormItem = (formItemId, updates) => {
    setFormItems(prevItems => {
      const updatedItems = prevItems.map(item => 
        item.id === formItemId ? { ...item, ...updates } : item
      );
      
      formItemsRef.current = updatedItems;
      
      if (formItemSaveTimeoutsRef.current[formItemId]) {
        clearTimeout(formItemSaveTimeoutsRef.current[formItemId]);
      }
      
      const updatedItem = updatedItems.find(item => item.id === formItemId);
      const itemCanSave = updatedItem && updatedItem.quantity >= 1 && updatedItem.unit_price >= 0 &&
        (updatedItem.sku_id || updatedItem.description?.trim()) && order?.id;
      if (itemCanSave) {
        formItemSaveTimeoutsRef.current[formItemId] = setTimeout(async () => {
          const currentItem = formItemsRef.current.find(item => item.id === formItemId);
          const currentCanSave = currentItem && currentItem.quantity >= 1 && currentItem.unit_price >= 0 &&
            (currentItem.sku_id || currentItem.description?.trim());
          if (currentCanSave) {
            await handleSaveFormItem(formItemId);
          }
          delete formItemSaveTimeoutsRef.current[formItemId];
        }, 2000);
      }
      
      return updatedItems;
    });
  };

  const handleUpdateLine = async (lineId, description) => {
    if (!order?.id) return;
    setLoading(true);
    try {
      await updateOrderLine(order.id, lineId, { description: description || '' });
      setSnackbar({ open: true, message: translate('orders.updated'), severity: 'success' });
      loadOrder();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!order?.id) return;
    setLoading(true);
    try {
      await deleteOrderLine(order.id, lineId);
      setSnackbar({ open: true, message: translate('orders.group_deleted') || 'Grupo removido.', severity: 'success' });
      setConfirmDelete({ open: false, type: null, lineId: null, itemId: null });
      loadOrder();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteLineItem = async (lineId, itemId) => {
    if (!order?.id) return;
    setLoading(true);
    try {
      await deleteOrderLineItem(order.id, lineId, itemId);
      setSnackbar({ open: true, message: translate('orders.item_deleted') || 'Item removido.', severity: 'success' });
      setConfirmDelete({ open: false, type: null, lineId: null, itemId: null });
      await loadOrder();
      
      const updatedOrder = await getOrder(order.id);
      if (!updatedOrder.data?.lines || updatedOrder.data.lines.length === 0) {
        await addOrderLine(order.id, { sequence: 1, description: '' });
        await loadOrder();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };


  const allSkus = products.flatMap((p) => (skusByProduct[p.id] || []).map((s) => ({ ...s, productName: p.name })));

  const handleItemCustomizationValueChange = (formItemId, key, value, formItem, extraUpdates = {}) => {
    const itemId = formItem.itemId;
    const lineId = formItem.lineId;
    if (!itemId || !lineId || !order?.id) return;

    setFormItems((prev) => {
      const updated = prev.map((item) =>
        item.id === formItemId
          ? {
              ...item,
              ...extraUpdates,
              customizationValues: { ...(item.customizationValues || {}), [key]: value },
            }
          : item
      );
      formItemsRef.current = updated;
      return updated;
    });

    const timeoutKey = `${formItemId}_${key}`;
    if (itemCustomizationSaveTimeoutsRef.current[timeoutKey]) {
      clearTimeout(itemCustomizationSaveTimeoutsRef.current[timeoutKey]);
    }
    itemCustomizationSaveTimeoutsRef.current[timeoutKey] = setTimeout(async () => {
      const currentItem = formItemsRef.current.find((i) => i.id === formItemId);
      if (!currentItem?.itemId || !currentItem?.lineId) return;
      const fields = (currentItem.customization_fields || []).map((f) => ({
        key: f.key,
        name: f.name,
        value_type: f.value_type,
        value: (currentItem.customizationValues || {})[f.key] ?? '',
        hide_in_public_form: !!f.hide_in_public_form,
      }));
      try {
        await upsertOrderLineItemCustomizationFields(order.id, currentItem.lineId, currentItem.itemId, fields);
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      }
      delete itemCustomizationSaveTimeoutsRef.current[timeoutKey];
    }, 800);
  };

  const handleAddItemCustomField = async () => {
    const formItemId = newItemCustomFieldModal.formItemId;
    if (!formItemId || !order?.id) return;
    const formItem = formItemsRef.current.find((i) => i.id === formItemId);
    if (!formItem?.itemId || !formItem?.lineId) return;
    const key = (newItemCustomField.key || newItemCustomField.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'campo';
    const name = (newItemCustomField.name || key).trim() || key;
    if (!key) return;
    try {
      const currentFields = formItem.customization_fields || [];
      const fields = [
        ...currentFields.map((f) => ({
          key: f.key,
          name: f.name,
          value_type: f.value_type,
          value: (formItem.customizationValues || {})[f.key] ?? '',
          hide_in_public_form: !!f.hide_in_public_form,
        })),
        { key, name, value_type: newItemCustomField.value_type || 'text', value: '', hide_in_public_form: false },
      ];
      await upsertOrderLineItemCustomizationFields(order.id, formItem.lineId, formItem.itemId, fields);
      setFormItems((prev) =>
        prev.map((it) =>
          it.id === formItemId
            ? {
                ...it,
                customization_fields: [...currentFields, { id: null, key, name, value_type: newItemCustomField.value_type || 'text', value: '', product_id: null, hide_in_public_form: false }],
                customizationValues: { ...(it.customizationValues || {}), [key]: '' },
              }
            : it
        )
      );
      formItemsRef.current = formItemsRef.current.map((it) =>
        it.id === formItemId
          ? {
              ...it,
              customization_fields: [...currentFields, { id: null, key, name, value_type: newItemCustomField.value_type || 'text', value: '', product_id: null }],
              customizationValues: { ...(it.customizationValues || {}), [key]: '' },
            }
          : it
      );
      setNewItemCustomFieldModal({ open: false, formItemId: null });
      setNewItemCustomField({ name: '', key: '', value_type: 'text' });
      const res = await getOrder(order.id);
      const newItemFromOrder = res.data?.lines?.flatMap((l) => l.items || []).find((i) => i.id === formItem.itemId);
      if (newItemFromOrder?.customization_fields) {
        const itemCustomizationValues = Object.fromEntries(newItemFromOrder.customization_fields.map((f) => [f.key, f.value || '']));
        setFormItems((prev) =>
          prev.map((it) =>
            it.id === formItemId
              ? { ...it, customization_fields: newItemFromOrder.customization_fields, customizationValues: itemCustomizationValues }
              : it
          )
        );
        formItemsRef.current = formItemsRef.current.map((it) =>
          it.id === formItemId ? { ...it, customization_fields: newItemFromOrder.customization_fields, customizationValues: itemCustomizationValues } : it
        );
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleToggleHideInPublicForm = async (formItemId, fieldKey, formItem) => {
    if (!formItem?.itemId || !formItem?.lineId || !order?.id) return;
    const currentFields = formItem.customization_fields || [];
    const updatedFields = currentFields.map((f) =>
      f.key === fieldKey ? { ...f, hide_in_public_form: !f.hide_in_public_form } : f
    );
    setFormItems((prev) =>
      prev.map((it) => (it.id === formItemId ? { ...it, customization_fields: updatedFields } : it))
    );
    formItemsRef.current = formItemsRef.current.map((it) =>
      it.id === formItemId ? { ...it, customization_fields: updatedFields } : it
    );
    const fields = updatedFields.map((f) => ({
      key: f.key,
      name: f.name,
      value_type: f.value_type,
      value: (formItem.customizationValues || {})[f.key] ?? '',
      hide_in_public_form: !!f.hide_in_public_form,
    }));
    try {
      await upsertOrderLineItemCustomizationFields(order.id, formItem.lineId, formItem.itemId, fields);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleRemoveItemCustomField = async (formItemId, fieldId) => {
    const formItem = formItemsRef.current.find((i) => i.id === formItemId);
    if (!formItem?.itemId || !formItem?.lineId || !order?.id || !fieldId) return;
    try {
      await deleteOrderLineItemCustomizationField(order.id, formItem.lineId, formItem.itemId, fieldId);
      const updatedFields = (formItem.customization_fields || []).filter((f) => f.id !== fieldId);
      const updatedValues = { ...(formItem.customizationValues || {}) };
      const removedField = (formItem.customization_fields || []).find((f) => f.id === fieldId);
      if (removedField) delete updatedValues[removedField.key];
      setFormItems((prev) =>
        prev.map((it) =>
          it.id === formItemId ? { ...it, customization_fields: updatedFields, customizationValues: updatedValues } : it
        )
      );
      formItemsRef.current = formItemsRef.current.map((it) =>
        it.id === formItemId ? { ...it, customization_fields: updatedFields, customizationValues: updatedValues } : it
      );
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const subtotal = formItems.reduce((sum, item) => {
    const qty = item.quantity || 0;
    const price = item.unit_price || 0;
    return sum + qty * price;
  }, 0);
  const discountValue = formData.discount_value ?? 0;
  const finalValue = Math.max(0, subtotal - discountValue);
  const discountPercent = subtotal > 0 ? (discountValue / subtotal) * 100 : 0;

  const applyDiscountFromPercent = (pctStr) => {
    const parsed = parsePercent(pctStr);
    if (parsed === '' || pctStr.trim() === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const pct = parseFloat(parsed);
    const dv = Math.max(0, Math.min(subtotal, subtotal * (pct / 100)));
    setFormData((f) => ({ ...f, discount_value: dv }));
  };

  const applyDiscountFromValue = (valueStr) => {
    if (!valueStr || valueStr.trim() === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const parsed = parseCurrency(valueStr);
    if (parsed === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const num = parseFloat(parsed);
    const dv = Math.max(0, Math.min(subtotal, num));
    setFormData((f) => ({ ...f, discount_value: dv }));
  };

  const applyDiscountFromFinalValue = (valueStr) => {
    if (!valueStr || valueStr.trim() === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const parsed = parseCurrency(valueStr);
    if (parsed === '') {
      setFormData((f) => ({ ...f, discount_value: null }));
      return;
    }
    const num = parseFloat(parsed);
    const finalVal = Math.max(0, Math.min(subtotal, num));
    const dv = subtotal - finalVal;
    setFormData((f) => ({ ...f, discount_value: dv }));
  };

  const statusOptionKeys = {
    DRAFT: 'orders.draft',
    SENT: 'orders.sent',
    AWAITING_FORM: 'orders.awaiting_form',
    APPROVED: 'orders.approved',
    CONFIRMED: 'orders.confirmed',
    IN_PREPARATION: 'orders.in_preparation',
    DELIVERED: 'orders.delivered',
    REJECTED: 'orders.rejected',
    EXPIRED: 'orders.expired',
    CANCELLED: 'orders.cancelled',
  };
  const editableStatuses = ['DRAFT', 'SENT', 'AWAITING_FORM', 'APPROVED', 'CONFIRMED', 'IN_PREPARATION', 'DELIVERED', 'REJECTED', 'EXPIRED', 'CANCELLED'];

  if (creating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>{translate('common.loading')}</Typography>
      </Box>
    );
  }

  if (!order && id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>{translate('common.loading')}</Typography>
      </Box>
    );
  }

  if (!order && !id) {
    return null;
  }

  const summaryPanel = (
    <Paper elevation={2} sx={{ p: 2, width: { xs: '100%', md: 300 }, flexShrink: 0, alignSelf: 'flex-start', position: { xs: 'relative', md: 'sticky' }, top: { md: 88 } }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {translate('orders.order_summary') || 'Resumo do pedido'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {translate('orders.subtotal') || 'Subtotal'}
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {formatCurrency(subtotal)}
          </Typography>
        </Box>
        <TextField
          size="small"
          label={translate('orders.discount_percent') || 'Desconto (%)'}
          type="text"
          inputMode="decimal"
          value={discountFieldFocused === 'percent' ? discountPercentInput : (discountPercent > 0 ? formatPercent(discountPercent) : '')}
          onChange={(e) => handlePercentInputChange(e, (synth) => setDiscountPercentInput(synth.target?.value ?? ''))}
          onFocus={() => {
            setDiscountFieldFocused('percent');
            setDiscountPercentInput(discountPercent > 0 ? formatPercent(discountPercent) : '');
          }}
          onBlur={() => {
            applyDiscountFromPercent(discountPercentInput);
            setDiscountFieldFocused(null);
            setDiscountPercentInput('');
          }}
          placeholder="0"
          sx={{ width: '100%' }}
        />
        <TextField
          size="small"
          label={translate('orders.discount_value') || 'Desconto (R$)'}
          value={discountFieldFocused === 'value' ? discountValueInput : (discountValue > 0 ? formatCurrency(discountValue) : '')}
          onChange={(e) => handleCurrencyInputChange(e, (synth) => {
            if (typeof synth === 'string') {
              setDiscountValueInput('');
              return;
            }
            setDiscountValueInput(synth.target.value);
          })}
          onFocus={() => {
            setDiscountFieldFocused('value');
            setDiscountValueInput(discountValue > 0 ? formatCurrency(discountValue) : '');
          }}
          onBlur={() => {
            applyDiscountFromValue(discountValueInput);
            setDiscountFieldFocused(null);
            setDiscountValueInput('');
          }}
          placeholder="R$ 0,00"
          sx={{ width: '100%' }}
        />
        <TextField
          size="small"
          label={translate('orders.final_value') || 'Valor final'}
          value={discountFieldFocused === 'final' ? finalValueInput : formatCurrency(finalValue)}
          onChange={(e) => handleCurrencyInputChange(e, (synth) => {
            if (typeof synth === 'string') {
              setFinalValueInput('');
              return;
            }
            setFinalValueInput(synth.target.value);
          })}
          onFocus={() => {
            setDiscountFieldFocused('final');
            setFinalValueInput(formatCurrency(finalValue));
          }}
          onBlur={() => {
            applyDiscountFromFinalValue(finalValueInput);
            setDiscountFieldFocused(null);
            setFinalValueInput('');
          }}
          placeholder="R$ 0,00"
          sx={{ width: '100%' }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight="bold">
            {translate('orders.final_value') || 'Valor final'}
          </Typography>
          <Typography variant="h6" color="primary">
            {formatCurrency(finalValue)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {order?.number ? `${order.number} – ` : ''}{isNewOrder ? translate('orders.create') : translate('orders.edit')}
        </Typography>

        <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            freeSolo
            options={customers}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return (option.name || '') + (option.email ? ' – ' + option.email : '');
            }}
            value={customers.find((c) => c.id === formData.customer_id) || customerInputText || null}
            onChange={handleCustomerChange}
            onInputChange={(_, value) => setCustomerInputText(value)}
            inputValue={customerInputText}
            sx={{ minWidth: 300, flex: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('orders.customer')}
                size="small"
              />
            )}
          />
          <TextField
            size="small"
            label={translate('orders.order_title')}
            value={formData.title || ''}
            onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
            placeholder={translate('orders.order_title_placeholder') || 'Ex: miniatura boneco tamanho X'}
            inputProps={{ maxLength: 255 }}
            disabled={formData.status !== 'DRAFT' && formData.status !== 'SENT'}
            sx={{ minWidth: 250, flex: 1 }}
          />
          <TextField
            size="small"
            label={translate('orders.valid_until')}
            value={formData.valid_until}
            onChange={(e) => {
              handleDateInputChange(e, (synth) => {
                setFormData((f) => ({ ...f, valid_until: synth.target.value }));
              });
            }}
            placeholder="DD/MM/AAAA"
            inputProps={{ maxLength: 10 }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            label={translate('orders.created_at')}
            value={formData.created_at}
            onChange={(e) => {
              handleDateTimeInputChange(e, (synth) => {
                setFormData((f) => ({ ...f, created_at: synth.target.value }));
              });
            }}
            disabled={!['DRAFT', 'SENT'].includes(formData.status)}
            placeholder={getDateTimeInputPlaceholder()}
            inputProps={{ maxLength: 16 }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            select
            label={translate('orders.status')}
            value={formData.status}
            onChange={(e) => setFormData((f) => ({ ...f, status: e.target.value }))}
            sx={{ minWidth: 140 }}
          >
            {editableStatuses.map((s) => (
              <MenuItem key={s} value={s}>
                {translate(statusOptionKeys[s] || 'orders.status')}
              </MenuItem>
            ))}
          </TextField>
          {formData.status === 'AWAITING_FORM' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                try {
                  const res = await getOrder(order.id);
                  const token = res?.data?.form_token;
                  const wsId = currentWorkspace?.id;
                  if (!token) {
                    setSnackbar({ open: true, message: translate('orders.save_first_for_link') || 'Salve o pedido para gerar o link.', severity: 'info' });
                  } else if (!wsId) {
                    setSnackbar({ open: true, message: translate('orders.select_workspace_for_link') || 'Selecione um espaço de trabalho.', severity: 'info' });
                  } else {
                    const url = `${window.location.origin}/formulario/${wsId}/${token}`;
                    await navigator.clipboard.writeText(url);
                    setSnackbar({ open: true, message: translate('orders.form_link_copied') || 'Link copiado!', severity: 'success' });
                    setOrder((o) => (o ? { ...o, form_token: token } : o));
                  }
                } catch (err) {
                  setSnackbar({ open: true, message: err.message, severity: 'error' });
                }
              }}
            >
              {translate('orders.copy_form_link') || 'Copiar link do formulário'}
            </Button>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
        {order.id && (
          <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {translate('orders.attachments') || 'Anexos'}
            </Typography>
            <Box>
              <MediaGallery
                metadataKey="order_attachment"
                metadataValue={order.id}
                accept="*/*"
                readOnly={false}
                refreshKey={mediaRefreshKey}
              />
            </Box>
          </Paper>
        )}
        <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {translate('orders.observations') || 'Observações'}
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder={translate('orders.observations_placeholder') || 'Anotações internas e histórico de conversa com o cliente'}
              value={formData.observations || ''}
              onChange={(e) => setFormData((f) => ({ ...f, observations: e.target.value }))}
              inputProps={{ maxLength: 10000 }}
              size="small"
              sx={{
                flex: 1,
                '& .MuiInputBase-root': { flex: 1, alignItems: 'flex-start' },
                '& .MuiInputBase-input': { flex: 1, minHeight: 0 },
              }}
            />
          </Box>
        </Paper>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          {translate('orders.add_item') || 'Adicionar Item'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, '& > *:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider', pb: 1 } }}>
          {formItems.map((formItem) => (
            <Box key={formItem.id} sx={{ py: 1, px: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Autocomplete
                options={allSkus}
                getOptionLabel={(option) => {
                  const base = `${option.code || option.id} – ${option.productName} (${formatCurrency(option.price)})`;
                  const attrs = option.attributes;
                  if (attrs && typeof attrs === 'object' && Object.keys(attrs).length > 0) {
                    const attrStr = Object.entries(attrs)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(' | ');
                    return `${base} – ${attrStr}`;
                  }
                  return base;
                }}
                value={allSkus.find((s) => s.id === formItem.sku_id) || null}
                onChange={(_, option) => {
                  const currentItem = formItemsRef.current.find(item => item.id === formItem.id);
                  const hasCustomPrice = currentItem && (
                    (currentItem.unit_priceDisplay && currentItem.unit_priceDisplay !== '') ||
                    (currentItem.unit_price > 0 && (!currentItem.unit_priceDisplay || currentItem.unit_priceDisplay === ''))
                  );
                  
                  if (hasCustomPrice && currentItem.unit_price > 0) {
                    handleUpdateFormItem(formItem.id, {
                      sku_id: option?.id || '',
                    });
                  } else {
                    handleUpdateFormItem(formItem.id, {
                      sku_id: option?.id || '',
                      unit_price: option?.price || 0,
                      unit_priceDisplay: option?.price ? formatCurrency(option.price) : ''
                    });
                  }
                }}
                sx={{ minWidth: 320, flex: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label={translate('orders.sku')} size="small" />
                )}
              />
              <TextField
                size="small"
                type="number"
                label={translate('orders.quantity')}
                value={formItem.quantity}
                onChange={(e) => handleUpdateFormItem(formItem.id, { quantity: parseInt(e.target.value, 10) || 1 })}
                onBlur={() => {
                  const currentItem = formItemsRef.current.find(item => item.id === formItem.id);
                  const canSave = currentItem && currentItem.quantity >= 1 && currentItem.unit_price >= 0 && (currentItem.sku_id || currentItem.description?.trim()) && order?.id;
                  if (canSave) {
                    handleSaveFormItem(formItem.id);
                  }
                }}
                sx={{ width: 100 }}
                inputProps={{ min: 1 }}
              />
              <TextField
                size="small"
                label={translate('orders.unit_price')}
                value={formItem.unit_priceDisplay || ''}
                onChange={(e) => {
                  handleCurrencyInputChange(e, (synth) => {
                    if (typeof synth === 'string') {
                      handleUpdateFormItem(formItem.id, { unit_price: 0, unit_priceDisplay: '' });
                    } else {
                      handleUpdateFormItem(formItem.id, { unit_price: synth.numericValue, unit_priceDisplay: synth.target.value });
                    }
                  });
                }}
                onFocus={() => {
                  if (!formItem.unit_priceDisplay && formItem.unit_price > 0) {
                    const displayValue = formatCurrency(formItem.unit_price);
                    handleUpdateFormItem(formItem.id, { unit_priceDisplay: displayValue });
                  }
                }}
                onBlur={() => {
                  if (formItem.unit_priceDisplay && formItem.unit_priceDisplay.trim() !== '') {
                    const numericValue = parseFloat(formItem.unit_priceDisplay.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
                    if (numericValue > 0) {
                      const formattedDisplay = formatCurrency(numericValue);
                      if (formItem.unit_priceDisplay !== formattedDisplay) {
                        handleUpdateFormItem(formItem.id, { unit_price: numericValue, unit_priceDisplay: formattedDisplay });
                      } else if (numericValue !== formItem.unit_price) {
                        handleUpdateFormItem(formItem.id, { unit_price: numericValue });
                      }
                    }
                  } else if (formItem.unit_price > 0) {
                    handleUpdateFormItem(formItem.id, { unit_priceDisplay: formatCurrency(formItem.unit_price) });
                  }
                  const currentItem = formItemsRef.current.find(item => item.id === formItem.id);
                  const canSave = currentItem && currentItem.quantity >= 1 && currentItem.unit_price >= 0 && (currentItem.sku_id || currentItem.description?.trim()) && order?.id;
                  if (canSave) {
                    handleSaveFormItem(formItem.id);
                  }
                }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label={translate('orders.item_description') || 'Descrição/Observação'}
                placeholder={formItem.sku_id ? (translate('orders.item_description_placeholder') || 'Observações adicionais (opcional)') : (translate('orders.item_description_required_free') || 'Descrição obrigatória para itens sem produto')}
                value={formItem.description || ''}
                onChange={(e) => handleUpdateFormItem(formItem.id, { description: e.target.value })}
                onBlur={() => {
                  const currentItem = formItemsRef.current.find(item => item.id === formItem.id);
                  const canSave = currentItem && currentItem.quantity >= 1 && currentItem.unit_price >= 0 && (currentItem.sku_id || currentItem.description?.trim()) && order?.id;
                  if (canSave) {
                    handleSaveFormItem(formItem.id);
                  }
                }}
                multiline
                rows={1}
                sx={{ minWidth: 200, flex: 1 }}
              />
              <IconButton
                onClick={() => handleRemoveFormItem(formItem.id)}
                disabled={loading || (formItems.length === 1 && !formItem.itemId)}
                sx={{ height: '40px' }}
                title={translate('common.delete')}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
            {formItem.itemId && (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'text.primary' },
                  }}
                  onClick={() => setItemCustomizationExpanded((p) => ({ ...p, [formItem.id]: !p[formItem.id] }))}
                >
                  {itemCustomizationExpanded[formItem.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  <Typography variant="body2">
                    {translate('orders.customizations') || 'Customizações'}
                    {(formItem.customization_fields?.length || 0) > 0 && ` (${formItem.customization_fields.length})`}
                  </Typography>
                </Box>
                <Collapse in={!!itemCustomizationExpanded[formItem.id]}>
                  <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(formItem.customization_fields || []).map((f) => (
                      <Box key={f.id || f.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 200, flex: '1 1 200px', maxWidth: 360 }}>
                    {f.value_type === 'textarea' ? (
                      <TextField
                        fullWidth
                        size="small"
                        label={f.name}
                        value={(formItem.customizationValues || {})[f.key] || ''}
                        onChange={(e) => handleItemCustomizationValueChange(formItem.id, f.key, e.target.value, formItem)}
                        multiline
                        rows={2}
                      />
                    ) : f.value_type === 'number' ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={f.name}
                        value={(formItem.customizationValues || {})[f.key] || ''}
                        onChange={(e) => handleItemCustomizationValueChange(formItem.id, f.key, e.target.value, formItem)}
                      />
                    ) : f.value_type === 'date' ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label={f.name}
                        value={(formItem.customizationValues || {})[f.key] || ''}
                        onChange={(e) => handleItemCustomizationValueChange(formItem.id, f.key, e.target.value, formItem)}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : f.value_type === 'select' && f.options && f.options.length > 0 ? (
                      <Box sx={{ minWidth: 180, flex: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label={f.name}
                          value={
                            f.allow_other && ((formItem.selectOtherActive || {})[f.key] || ((formItem.customizationValues || {})[f.key] && !(f.options || []).includes((formItem.customizationValues || {})[f.key])))
                              ? '__outro__'
                              : ((formItem.customizationValues || {})[f.key] || '')
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            const opts = f.options || [];
                            const currentVal = (formItem.customizationValues || {})[f.key] || '';
                            if (v === '__outro__') {
                              handleItemCustomizationValueChange(
                                formItem.id,
                                f.key,
                                currentVal && !opts.includes(currentVal) ? currentVal : '',
                                formItem,
                                { selectOtherActive: { ...(formItem.selectOtherActive || {}), [f.key]: true } }
                              );
                            } else {
                              handleItemCustomizationValueChange(
                                formItem.id,
                                f.key,
                                v,
                                formItem,
                                { selectOtherActive: { ...(formItem.selectOtherActive || {}), [f.key]: false } }
                              );
                            }
                          }}
                        >
                          <MenuItem value="">{translate('common.select') || 'Selecione'}</MenuItem>
                          {(f.options || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                          {f.allow_other && <MenuItem value="__outro__">{translate('orders.other') || 'Outro'}</MenuItem>}
                        </TextField>
                        {f.allow_other && ((formItem.selectOtherActive || {})[f.key] || ((formItem.customizationValues || {})[f.key] && !(f.options || []).includes((formItem.customizationValues || {})[f.key]))) && (
                          <TextField
                            fullWidth
                            size="small"
                            label={translate('orders.other_value') || 'Especifique'}
                            value={(formItem.customizationValues || {})[f.key] || ''}
                            onChange={(e) => handleItemCustomizationValueChange(formItem.id, f.key, e.target.value, formItem)}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        label={f.name}
                        value={(formItem.customizationValues || {})[f.key] || ''}
                        onChange={(e) => handleItemCustomizationValueChange(formItem.id, f.key, e.target.value, formItem)}
                      />
                    )}
                        </Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={!!f.hide_in_public_form}
                              onChange={() => handleToggleHideInPublicForm(formItem.id, f.key, formItem)}
                            />
                          }
                          label={translate('orders.hide_in_public_form_short') || 'Ocultar'}
                          sx={{ flexShrink: 0 }}
                          title={translate('orders.hide_in_public_form') || 'Ocultar no formulário público'}
                        />
                        {!f.product_id && f.id && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItemCustomField(formItem.id, f.id)}
                            sx={{ flexShrink: 0 }}
                            title={translate('common.delete')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setNewItemCustomField({ name: '', key: '', value_type: 'text' });
                        setNewItemCustomFieldModal({ open: true, formItemId: formItem.id });
                      }}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {translate('orders.add_custom_field') || 'Adicionar campo'}
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddNewFormLine}
            disabled={loading}
            sx={{ alignSelf: 'flex-start' }}
          >
            {translate('orders.add_new_line')}
          </Button>
        </Box>
      </Paper>

      <Dialog open={newItemCustomFieldModal.open} onClose={() => setNewItemCustomFieldModal({ open: false, formItemId: null })}>
        <DialogTitle>{translate('orders.add_custom_field') || 'Adicionar campo'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 320 }}>
            <TextField
              fullWidth
              size="small"
              label={translate('orders.field_name') || 'Nome do campo'}
              value={newItemCustomField.name}
              onChange={(e) => setNewItemCustomField((p) => ({ ...p, name: e.target.value }))}
              placeholder="Ex: Nome para gravação"
            />
            <TextField
              fullWidth
              size="small"
              select
              label={translate('orders.field_type') || 'Tipo'}
              value={newItemCustomField.value_type}
              onChange={(e) => setNewItemCustomField((p) => ({ ...p, value_type: e.target.value }))}
            >
              <MenuItem value="text">Texto</MenuItem>
              <MenuItem value="number">Número</MenuItem>
              <MenuItem value="date">Data</MenuItem>
              <MenuItem value="textarea">Texto longo</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewItemCustomFieldModal({ open: false, formItemId: null })}>{translate('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAddItemCustomField} disabled={!newItemCustomField.name?.trim()}>
            {translate('common.add') || 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/orders')}>
            {translate('orders.back_to_list')}
          </Button>
        </Box>
      </Box>

      {summaryPanel}

      <ConfirmDialog
        open={confirmDelete.open}
        title={translate('common.confirm_delete_title')}
        message={confirmDelete.type === 'line' ? (translate('orders.confirm_delete_group') || 'Remover este grupo e todos os itens?') : translate('orders.confirm_delete_item')}
        onConfirm={() => {
          if (confirmDelete.type === 'line' && confirmDelete.lineId) handleDeleteLine(confirmDelete.lineId);
          if (confirmDelete.type === 'item' && confirmDelete.lineId && confirmDelete.itemId) handleDeleteLineItem(confirmDelete.lineId, confirmDelete.itemId);
        }}
        onCancel={() => setConfirmDelete({ open: false, type: null, lineId: null, itemId: null })}
      />
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderFormPage;
